import React from 'react';
import { useImpressionManager } from '@fiverr-private/impressionable';
import { Box } from '@fiverr-private/theme';
import { useIsClient } from '@fiverr-private/hooks';
import { lazyImport } from '../../../apps/logged_out_homepage/utils/newLazyImport';
import Categories from '../../../apps/logged_out_homepage/components/Categories';
import Seo from '../../../apps/logged_out_homepage/shared/Seo';
import EntryWrapper from '../../../apps/logged_out_homepage/components/EntryWrapper';
import SubcategoryCarousel from '../../../apps/logged_out_homepage/components/SubcategoryCarousel';
import { getSubCategories } from '../../../apps/logged_out_homepage/components/SubcategoryCarousel/config';
import { ROLLOUTS } from '../../../apps/logged_out_homepage/utils/types/rollouts';
import { shouldLoadPersonalizedSection } from '../../../apps/logged_out_homepage/components/PersonalizedSectionWrapper/shouldLoad';
import VideoHeroBannerWrapper from '../../../apps/logged_out_homepage/components/VideoHeroBannerWrapper';
import { LAZY_COMPONENTS } from './constants';

const FiverrGuides = lazyImport(
    LAZY_COMPONENTS.FIVERR_GUIDES,
    () => import(/* webpackChunkName: 'FiverrGuides' */ '../../../apps/logged_out_homepage/components/FiverrGuides')
);

const Signup = lazyImport(
    LAZY_COMPONENTS.SIGNUP,
    () => import(/* webpackChunkName: 'Signup' */ '../../../apps/logged_out_homepage/components/Signup')
);

const PersonalizedSectionWrapper = lazyImport(
    LAZY_COMPONENTS.PERSONALIZED_SECTION_WRAPPER,
    () =>
        import(
            /* webpackChunkName: 'PersonalizedSectionWrapper' */ '../../../apps/logged_out_homepage/components/PersonalizedSectionWrapper'
        )
);

const Freelancers = lazyImport(
    LAZY_COMPONENTS.FREELANCERS,
    () => import(/* webpackChunkName: 'Freelancers' */ '../../../apps/logged_out_homepage/components/Freelancers')
);

const Video = lazyImport(
    LAZY_COMPONENTS.VIDEO,
    () => import(/* webpackChunkName: 'Video' */ '../../../apps/logged_out_homepage/components/Video')
);

const ProBanner = lazyImport(
    LAZY_COMPONENTS.PRO_BANNER,
    () => import(/* webpackChunkName: 'ProBanner' */ '../../../apps/logged_out_homepage/components/ProBannerSection')
);

const AiBanner = lazyImport(
    LAZY_COMPONENTS.AI_BANNER,
    () => import(/* webpackChunkName: 'AiBanner' */ '../../../apps/logged_out_homepage/components/AiBannerSection')
);

interface LoggedOutHomepageClientProps {
    userWasLoggedIn?: boolean;
    withEnglishBAL?: boolean;
    rollouts?: Record<string, boolean>;
}

const LoggedOutHomepageClient = ({ userWasLoggedIn, withEnglishBAL, rollouts }: LoggedOutHomepageClientProps) => {
    useImpressionManager();
    const isClient = useIsClient();

    const shouldLoad = isClient ? shouldLoadPersonalizedSection({ userWasLoggedIn }) : false;
    const isTiktokShopActive = Boolean((rollouts || {})[ROLLOUTS.LYNXES_TIKTOK_SHOP_SERVICE]);

    return (
        <EntryWrapper withEnglishBAL={withEnglishBAL} isTiktokShopActive={isTiktokShopActive}>
            <Seo />
            <Box paddingBottom={{ default: '10', md: '7' }}>
                <VideoHeroBannerWrapper />
            </Box>
            <Categories />
            {shouldLoad && <PersonalizedSectionWrapper userWasLoggedIn={userWasLoggedIn} />}
            <SubcategoryCarousel subCategories={getSubCategories(withEnglishBAL, isTiktokShopActive)} />
            <Freelancers />
            <ProBanner />
            <AiBanner />
            <Video />
            <FiverrGuides />
            <Signup />
        </EntryWrapper>
    );
};

export default LoggedOutHomepageClient;
